import { __decorate } from "tslib";
import { html, LitElement } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { emitEvent } from '../../utilities/misc/events';
import { stylesUpload } from './styles';
const preventDefaults = (event) => {
    event.preventDefault();
    event.stopPropagation();
};
/**
 * @since 1.3.0
 * @status stable
 *
 * @tagname kemet-upload
 * @summary An interface for uploading files.
 *
 * @prop {string} slug - A unique identifier for the component
 * @prop {string} accept - Determines what file types are accepted
 * @prop {boolean} multiple - Allows for multiple files
 * @prop {boolean} over - Automatically is true when a file is being dragged over the upload area
 * @prop {string} heading - Descriptive text for the upload area
 * @prop {boolean} mobile - Displays the component in a mobile context
 * @prop {string} breakpoint - Controls the point at which the component is considered mobile
 * @prop {boolean} noDragDrop - Is true if drag and drop support is not detected
 * @prop {number} maxSize - The maximum file size for uploads
 * @prop {string} buttonLabel - The browse files button text
 *
 * @event kemet-upload-change - Fires when files have been added
 *
 * @csspart upload - The area where files are dropped.
 * @csspart heading - The description in the upload area.
 * @csspart button - The button in the upload area.
 * @csspart files - The area where uploaded files are listed.
 *
 * @cssproperty --kemet-upload-color - The default text color.
 * @cssproperty --kemet-upload-height - The height.
 * @cssproperty --kemet-upload-border - The border.
 * @cssproperty --kemet-upload-background-color - The background color.
 *
 */
let KemetUpload = class KemetUpload extends LitElement {
    constructor() {
        super(...arguments);
        this.slug = 'upload';
        this.heading = 'Drag & Drop Files';
        this.breakpoint = '600px';
        this.buttonLabel = 'Browse Files';
    }
    firstUpdated() {
        this.fileInputElement = this.shadowRoot.querySelector('[type="file"]');
        this.upload = this.shadowRoot.querySelector('.upload');
        ['dragenter', 'dragover', 'dragleave', 'drop'].forEach((eventName) => {
            this.upload.addEventListener(eventName, event => preventDefaults(event), false);
        });
        ['dragenter', 'dragover'].forEach((eventName) => {
            this.upload.addEventListener(eventName, () => { this.over = true; }, false);
        });
        ['dragleave', 'drop'].forEach((eventName) => {
            this.upload.addEventListener(eventName, () => { this.over = false; }, false);
        });
        this.isMobile();
        this.hasDragDrop();
        window.addEventListener('resize', () => { this.isMobile(); });
        this.upload.addEventListener('drop', event => this.handleDrop(event), false);
    }
    render() {
        return html `
      <div class="upload" part="upload">
        <kemet-icon icon="cloud-arrow-up" size="128"></kemet-icon>
        <input
          type="file"
          id=${this.slug}
          ?multiple=${this.multiple}
          accept=${ifDefined(this.accept) ? this.accept : null}
          @change=${(event) => this.handleChange(event)}
        />
        <h3 class="heading" part="heading">${this.heading}</h3>
        <label class="button" part="button" for=${this.slug}>${this.buttonLabel}</label>
      </div>
      <div class="files" part="files">
        <slot></slot>
      </div>
    `;
    }
    handleChange(event) {
        emitEvent(this, 'kemet-upload-change', {
            event,
            files: this.fileInputElement.files,
            fileElement: this.fileInputElement,
        });
    }
    handleDrop(event) {
        emitEvent(this, 'kemet-upload-change', {
            event,
            files: (event === null || event === void 0 ? void 0 : event.dataTransfer.files) || [],
            fileElement: this.fileInputElement,
        });
    }
    isMobile() {
        const mediaQuery = window.matchMedia(`(max-width: ${this.breakpoint})`);
        this.mobile = mediaQuery.matches;
    }
    hasDragDrop() {
        this.noDragDrop = !('draggable' in document.createElement('span'));
    }
};
KemetUpload.styles = [stylesUpload];
__decorate([
    property({ type: String })
], KemetUpload.prototype, "slug", void 0);
__decorate([
    property({ type: String })
], KemetUpload.prototype, "accept", void 0);
__decorate([
    property({ type: Boolean })
], KemetUpload.prototype, "multiple", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], KemetUpload.prototype, "over", void 0);
__decorate([
    property({ type: String })
], KemetUpload.prototype, "heading", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], KemetUpload.prototype, "mobile", void 0);
__decorate([
    property({ type: String, reflect: true })
], KemetUpload.prototype, "breakpoint", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'no-drag-drop' })
], KemetUpload.prototype, "noDragDrop", void 0);
__decorate([
    property({ type: Number, attribute: 'max-size' })
], KemetUpload.prototype, "maxSize", void 0);
__decorate([
    property({ type: String, attribute: 'button-label' })
], KemetUpload.prototype, "buttonLabel", void 0);
__decorate([
    state()
], KemetUpload.prototype, "fileInputElement", void 0);
__decorate([
    state()
], KemetUpload.prototype, "upload", void 0);
KemetUpload = __decorate([
    customElement('kemet-upload')
], KemetUpload);
export default KemetUpload;
//# sourceMappingURL=kemet-upload.js.map